import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { attachOrganisationHeader } from '@/api/organisation';
import { storageKeys, BACKEND_API_URL } from '@/constants';
import { message } from 'ant-design-vue';

axios.defaults.headers.common = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
  'Cache-Control': 'no-cache',
};

axios.defaults.withCredentials = true;
axios.defaults.baseURL = BACKEND_API_URL;

const getAccessToken = () => localStorage.getItem(storageKeys.accessToken);

axios.interceptors.request.use(
  async (config) => {
    if (config.url.includes('auth')) {
      config.url = `/api${config.url}`;
      return config;
    }

    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
      };
    }

    // Org-panel endpoints reached through this client still need the
    // organisation they act on. Student endpoints (`students/api/…`) resolve
    // the organisation from the enrolment, so they are left untouched.
    if (config.url.replace(/^\//, '').startsWith('api/organisations/')) {
      return attachOrganisationHeader(config);
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

let refreshInProgress = false;
let failedQueue = [];
const max_retries = 3;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const accessToken = getAccessToken();
    const responseStatus = error.response?.status;
    error.config.retries = error.config.retries || {
      count: 0,
    };

    if (responseStatus >= 500) {
      // message.error('A server error occurred, Please try again later!');
      return Promise.reject(error);
    }
    if (
      responseStatus === 403 &&
      error.response?.data?.code !== 'TRIAL_LIMIT_REACHED'
    ) {
      message.error(
        'You do not have permission to access this page, Please contact admin!'
      );
      return Promise.reject(error);
    }

    if (responseStatus === 400 && error.response?.data?.toast_msg) {
      message.error(error.response.data.toast_msg);
      return Promise.reject(error);
    }

    if (
      error?.response?.status === 401 &&
      accessToken &&
      error.config.retries.count < max_retries
    ) {
      const authStore = useAuthStore();
      const redirectUrl = '/login?session_expired=true';
      if (
        !authStore.refreshToken ||
        error.response.config.url.includes('auth/refresh')
      ) {
        authStore.logoutUser(() => {
          window.location.href = redirectUrl;
        });
        return;
      }

      const originalRequest = error.config;

      if (refreshInProgress) {
        // Add the request to the failed queue and wait for token refresh to complete
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(async (token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      refreshInProgress = true;
      try {
        await authStore.refreshToken();
        error.config.retries.count += 1;
        processQueue(null, authStore.accessToken);
        return axios(error.config);
      } catch (error) {
        window.location.href = redirectUrl;
        return;
      } finally {
        refreshInProgress = false;
      }
    }
    return Promise.reject(error);
  }
);

export const post = async (endpoint, data = {}) => {
  try {
    const response = await axios.post(endpoint, data);
    return response;
  } catch (error) {
    const err = error;
    return err.response;
  }
};

export const upload = async (endpoint, data = {}, onUploadProgress) => {
  try {
    const response = await axios.post(endpoint, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    return response;
  } catch (error) {
    const err = error;
    return err.response;
  }
};

export const uploadPatch = async (endpoint, data = {}, onUploadProgress) => {
  try {
    const response = await axios.patch(endpoint, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    return response;
  } catch (error) {
    const err = error;
    return err.response;
  }
};

export const get = async (endpoint, params = {}) => {
  params = Object.keys(params).reduce((acc, key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});

  try {
    const response = await axios.get(endpoint, {
      params,
      // Serialize arrays as repeated keys (e.g. ?a=1&a=2) instead of ?a[]=1&a[]=2
      // so Django QueryDict.getlist() works correctly.
      paramsSerializer: { indexes: null },
    });
    return response;
  } catch (error) {
    const err = error;
    return err.response;
  }
};

// A file streamed by our API (e.g. a report PDF) as a Blob, for an object URL
// that pdf.js can read without any storage CORS.
export const getBlob = async (endpoint) => {
  try {
    return await axios.get(endpoint, { responseType: 'blob' });
  } catch (error) {
    return error.response;
  }
};

export const put = async (endpoint, data) => {
  try {
    const response = await axios.put(endpoint, data);
    return response;
  } catch (error) {
    const err = error;
    return err.response;
  }
};

export const patch = async (endpoint, data) => {
  try {
    const response = await axios.patch(endpoint, data);
    return response;
  } catch (error) {
    const err = error;
    return err.response;
  }
};

export const destroy = async (endpoint) => {
  try {
    const response = await axios.delete(endpoint);
    return response;
  } catch (error) {
    const err = error;
    return err.response;
  }
};

const buildApiUrl = (endpoint) => {
  if (/^https?:\/\//i.test(endpoint)) return endpoint;
  const base = BACKEND_API_URL.endsWith('/')
    ? BACKEND_API_URL
    : `${BACKEND_API_URL}/`;
  const path = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${base}${path}`;
};

const parseSseEvent = (rawEvent) => {
  const lines = rawEvent.split('\n');
  let eventType = 'message';
  const dataLines = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventType = line.slice(6).trim();
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  let parsedData = null;
  const payload = dataLines.join('\n').trim();
  if (payload) {
    try {
      parsedData = JSON.parse(payload);
    } catch {
      parsedData = { raw: payload };
    }
  }

  return { eventType, parsedData };
};

export const postStream = async (endpoint, data = {}, handlers = {}) => {
  const { onChunk, onDone, onError } = handlers;
  const accessToken = getAccessToken();
  // FormData (e.g. image attachments) must go up as multipart with a
  // browser-set boundary, so we omit the JSON Content-Type in that case.
  const isFormData = data instanceof FormData;
  const headers = {
    Accept: 'application/json, text/event-stream, */*',
    Authorization: `Bearer ${accessToken}`,
    'X-Requested-With': 'XMLHttpRequest',
    'Cache-Control': 'no-cache',
  };
  if (!isFormData) headers['Content-Type'] = 'application/json';
  const response = await fetch(buildApiUrl(endpoint), {
    method: 'POST',
    headers,
    body: isFormData ? data : JSON.stringify(data),
  });

  if (!response.ok) {
    let errorMessage = 'Something went wrong. Please try again later.';
    try {
      const errorBody = await response.json();
      errorMessage = errorBody?.error || errorBody?.detail || errorMessage;
    } catch {
      const text = await response.text();
      if (text) errorMessage = text;
    }
    throw new Error(errorMessage);
  }

  if (!response.body) {
    throw new Error('Empty streaming response body.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const handleEvent = (rawEvent) => {
    const trimmed = rawEvent.trim();
    if (!trimmed) return null;

    const { eventType, parsedData } = parseSseEvent(trimmed);
    if (eventType === 'chunk') {
      const delta =
        typeof parsedData?.delta === 'string' ? parsedData.delta : '';
      onChunk?.(delta);
      return null;
    }

    if (eventType === 'done') {
      onDone?.(parsedData || {});
      return parsedData || {};
    }

    if (eventType === 'error') {
      const streamError =
        parsedData?.error || 'Failed to process AskGPT query.';
      onError?.(streamError);
      throw new Error(streamError);
    }

    return null;
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    buffer = buffer.replace(/\r\n/g, '\n');

    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const eventChunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const donePayload = handleEvent(eventChunk);
      if (donePayload) return donePayload;
      boundary = buffer.indexOf('\n\n');
    }

    if (done) {
      const donePayload = handleEvent(buffer);
      if (donePayload) return donePayload;
      break;
    }
  }

  return null;
};

export const download = async (endpoint, params = {}) => {
  const accessToken = getAccessToken();
  const queryString = new URLSearchParams(params).toString();
  try {
    const response = await fetch(
      `${BACKEND_API_URL}${endpoint}?${queryString}&check_auth_with_token=1&`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const disposition = response.headers.get('Content-Disposition');
    let filename = disposition.split('filename=')[1];
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    console.log(url);
    const link = document.createElement('a');
    console.log(link);
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Error downloading the file', error);
    message.error('An error occurred! Please contact admin');
  }
};
